import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { format, differenceInDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  Wallet,
  Plus,
  TrendingUp,
  TrendingDown,
  ArrowDownLeft,
  ArrowUpRight,
  RefreshCw,
  CreditCard,
  Clock,
  AlertTriangle,
  Gift,
  Banknote,
} from 'lucide-react';

import { useAdWallet } from '../hooks/use-ad-wallet';
import { useWalletTransactions } from '../hooks/use-wallet-transactions';
import { useRechargeWallet } from '../hooks/use-recharge-wallet';
import type { AdTransaction } from '../types/ads.types';
import { rechargeSchema, type RechargeFormData } from '@/lib/validations/ads.schema';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/atoms/card';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import { Skeleton } from '@/components/atoms/skeleton';
import { MetricCard } from '@/components/organisms/cards';
import { EmptyState } from '@/components/organisms/empty-state';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/atoms/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/molecules/tabs';
import { formatPrice, cn, getAmountByCurrency } from '@/lib/utils';
import { useConfigStore } from '@/lib/stores/config.store';

const RECHARGE_AMOUNTS = [10, 25, 50, 100, 250, 500];
const PAYMENT_METHODS = [
  { value: 'card', label: 'ads.wallet.paymentMethods.card' },
  { value: 'paypal', label: 'ads.wallet.paymentMethods.paypal' },
  { value: 'mobile_money', label: 'ads.wallet.paymentMethods.mobileMoney' },
];

const TRANSACTION_FILTERS = ['all', 'recharge', 'spend', 'refund', 'bonus', 'admin_credit', 'cash_payment', 'expired'] as const;

export function WalletContainer() {
  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [transactionFilter, setTransactionFilter] = React.useState<string>('all');
  const [page] = React.useState(1);
  const { currency } = useConfigStore();

  const { data: wallet, isLoading: isLoadingWallet } = useAdWallet();
  const { data: transactions, isLoading: isLoadingTransactions } = useWalletTransactions({ page, limit: 20 });
  const { mutate: rechargeWallet, isPending: isRecharging } = useRechargeWallet();

  const form = useForm<RechargeFormData>({
    resolver: zodResolver(rechargeSchema),
    defaultValues: {
      amount: 50,
      paymentMethod: 'card',
    },
  });

  const onSubmit = (data: RechargeFormData) => {
    rechargeWallet(data, {
      onSuccess: () => {
        setIsDialogOpen(false);
        form.reset({ amount: 50, paymentMethod: 'card' });
      },
    });
  };

  const filteredTransactions = transactions?.filter(
    (t) => transactionFilter === 'all' || t.type === transactionFilter
  ) ?? [];

  // Calculate totals using selected currency
  const totalRecharges = transactions?.filter((t) => t.type === 'recharge').reduce((sum, t) => sum + getAmountByCurrency(currency, { eur: t.amountEur, usd: t.amountUsd, kmf: t.amountKmf }), 0) ?? 0;
  const totalSpent = transactions?.filter((t) => t.type === 'spend').reduce((sum, t) => sum + getAmountByCurrency(currency, { eur: t.amountEur, usd: t.amountUsd, kmf: t.amountKmf }), 0) ?? 0;

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header - Compact on mobile */}
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold truncate">{t('ads.wallet.title')}</h1>
          <p className="text-sm text-muted-foreground hidden sm:block">{t('ads.wallet.description')}</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="shrink-0 gap-1.5">
              <Plus className="size-4" />
              <span className="hidden sm:inline">{t('ads.wallet.recharge')}</span>
              <span className="sm:hidden">Recharger</span>
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{t('ads.wallet.rechargeTitle')}</DialogTitle>
              <DialogDescription>{t('ads.wallet.rechargeDescription')}</DialogDescription>
            </DialogHeader>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="space-y-2">
                <Label>{t('ads.wallet.selectAmount')}</Label>
                <div className="grid grid-cols-3 gap-2">
                  {RECHARGE_AMOUNTS.map((amount) => (
                    <Button
                      key={amount}
                      type="button"
                      variant={form.watch('amount') === amount ? 'default' : 'outline'}
                      onClick={() => form.setValue('amount', amount)}
                    >
                      {formatPrice(amount, currency)}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="customAmount">{t('ads.wallet.customAmount')}</Label>
                <Input
                  id="customAmount"
                  type="number"
                  min="1"
                  max="10000"
                  step="0.01"
                  {...form.register('amount', { valueAsNumber: true })}
                />
                {form.formState.errors.amount && (
                  <p className="text-sm text-destructive">{t(form.formState.errors.amount.message!)}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>{t('ads.wallet.paymentMethod')}</Label>
                <Select
                  value={form.watch('paymentMethod')}
                  onValueChange={(value) => form.setValue('paymentMethod', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map((method) => (
                      <SelectItem key={method.value} value={method.value}>
                        {t(method.label)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="rounded-lg bg-muted p-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('ads.wallet.totalToRecharge')}</span>
                  <span className="font-bold text-lg">{formatPrice(form.watch('amount') || 0, currency)}</span>
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" disabled={isRecharging}>
                  {isRecharging ? t('common.loading') : t('ads.wallet.confirmRecharge')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Balance Cards - Horizontal scroll on mobile */}
      {isLoadingWallet ? (
        <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 sm:grid sm:grid-cols-3 sm:gap-4 sm:overflow-visible scrollbar-hide">
          {[1, 2, 3].map((i) => (
            <div key={i} className="shrink-0 w-[200px] sm:w-auto">
              <Card className="p-4 h-full">
                <Skeleton className="h-20 w-full" />
              </Card>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 sm:grid sm:grid-cols-3 sm:gap-4 sm:overflow-visible scrollbar-hide">
          <div className="shrink-0 w-[200px] sm:w-auto">
            <Card className="border-primary h-full">
              <CardContent className="p-4 sm:pt-6">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs sm:text-sm font-medium text-muted-foreground">{t('ads.wallet.balance')}</p>
                    <p className="text-2xl sm:text-3xl font-bold truncate">{formatPrice(getAmountByCurrency(currency, { eur: wallet?.balanceEur, usd: wallet?.balanceUsd, kmf: wallet?.balanceKmf }), currency)}</p>
                    {wallet?.balanceExpiresAt && (
                      <ExpirationInfo expiresAt={wallet.balanceExpiresAt} />
                    )}
                  </div>
                  <div className="rounded-full bg-primary/10 p-2 sm:p-3 shrink-0">
                    <Wallet className="size-5 sm:size-6 text-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="shrink-0 w-[160px] sm:w-auto">
            <MetricCard
              title={t('ads.wallet.totalRecharged')}
              value={formatPrice(totalRecharges, currency)}
              icon={TrendingUp}
            />
          </div>

          <div className="shrink-0 w-[160px] sm:w-auto">
            <MetricCard
              title={t('ads.wallet.totalSpent')}
              value={formatPrice(totalSpent, currency)}
              icon={TrendingDown}
            />
          </div>
        </div>
      )}


      {/* Transactions */}
      <Card>
        <CardHeader className="p-4 sm:p-6 pb-3 sm:pb-4">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <CreditCard className="size-4 sm:size-5 text-primary" />
                {t('ads.wallet.transactions')}
              </CardTitle>
            </div>
            <CardDescription className="hidden sm:block">{t('ads.wallet.transactionsDescription')}</CardDescription>
            {/* Filter tabs - horizontal scroll on mobile */}
            <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-hide">
              <Tabs value={transactionFilter} onValueChange={setTransactionFilter}>
                <TabsList className="h-8 sm:h-10 inline-flex w-auto">
                  {TRANSACTION_FILTERS.map((filter) => (
                    <TabsTrigger key={filter} value={filter} className="px-2.5 sm:px-4 text-xs sm:text-sm whitespace-nowrap">
                      {t(`ads.wallet.filters.${filter}`)}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0">
          {isLoadingTransactions ? (
            <div className="space-y-2 sm:space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-16 sm:h-20 w-full rounded-xl" />
              ))}
            </div>
          ) : filteredTransactions.length > 0 ? (
            <div className="space-y-2 sm:space-y-3">
              {filteredTransactions.map((transaction) => (
                <TransactionItem key={transaction.refId} transaction={transaction} />
              ))}
            </div>
          ) : (
            <EmptyState
              icon={CreditCard}
              title={t('ads.wallet.noTransactions')}
              description={t('ads.wallet.noTransactionsDescription')}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

interface TransactionItemProps {
  transaction: AdTransaction;
}

function TransactionItem({ transaction }: TransactionItemProps) {
  const { t } = useTranslation();
  const { currency } = useConfigStore();

  const typeConfig: Record<AdTransaction['type'], { icon: typeof ArrowDownLeft; color: string; bgColor: string; label: string }> = {
    recharge: {
      icon: ArrowDownLeft,
      color: 'text-green-600',
      bgColor: 'bg-green-500/10',
      label: 'ads.wallet.transactionTypes.recharge',
    },
    spend: {
      icon: ArrowUpRight,
      color: 'text-red-600',
      bgColor: 'bg-red-500/10',
      label: 'ads.wallet.transactionTypes.spend',
    },
    refund: {
      icon: RefreshCw,
      color: 'text-blue-600',
      bgColor: 'bg-blue-500/10',
      label: 'ads.wallet.transactionTypes.refund',
    },
    bonus: {
      icon: Gift,
      color: 'text-purple-600',
      bgColor: 'bg-purple-500/10',
      label: 'ads.wallet.transactionTypes.bonus',
    },
    expired: {
      icon: Clock,
      color: 'text-orange-600',
      bgColor: 'bg-orange-500/10',
      label: 'ads.wallet.transactionTypes.expired',
    },
    admin_credit: {
      icon: Gift,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-500/10',
      label: 'ads.wallet.transactionTypes.adminCredit',
    },
    cash_payment: {
      icon: Banknote,
      color: 'text-green-600',
      bgColor: 'bg-green-500/10',
      label: 'ads.wallet.transactionTypes.cashPayment',
    },
  };

  const config = typeConfig[transaction.type];
  const Icon = config.icon;
  // Debit transactions: spend and expired reduce the balance
  // Credit transactions: recharge, refund, bonus, admin_credit, cash_payment increase the balance
  const isDebit = transaction.type === 'spend' || transaction.type === 'expired';
  const amount = Math.abs(getAmountByCurrency(currency, { eur: transaction.amountEur, usd: transaction.amountUsd, kmf: transaction.amountKmf }));

  return (
    <div className="flex items-center justify-between p-3 sm:p-4 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors border border-transparent hover:border-border">
      <div className="flex items-center gap-2.5 sm:gap-4 min-w-0">
        <div className={cn('rounded-lg sm:rounded-xl p-2 sm:p-2.5 shrink-0', config.bgColor)}>
          <Icon className={cn('size-4 sm:size-5', config.color)} />
        </div>
        <div className="min-w-0">
          <p className="font-medium text-sm sm:text-base truncate">{transaction.description || t(config.label)}</p>
          <p className="text-xs sm:text-sm text-muted-foreground">
            {format(new Date(transaction.createdAt), 'dd MMM', { locale: fr })}
            <span className="hidden sm:inline"> {format(new Date(transaction.createdAt), 'yyyy', { locale: fr })}</span>
            <span className="mx-1">•</span>
            {format(new Date(transaction.createdAt), 'HH:mm')}
          </p>
        </div>
      </div>
      <div className="text-right shrink-0 ml-2">
        <p className={cn(
          'font-semibold text-sm sm:text-lg tabular-nums',
          isDebit ? 'text-red-600' : 'text-green-600'
        )}>
          {isDebit ? '-' : '+'}
          {formatPrice(amount, currency)}
        </p>
        <p className="text-[10px] sm:text-xs text-muted-foreground mt-0.5 hidden sm:block">
          {t('ads.wallet.balanceAfter')}: <span className="font-medium">{formatPrice(getAmountByCurrency(currency, { eur: transaction.balanceAfterEur, usd: transaction.balanceAfterUsd, kmf: transaction.balanceAfterKmf }), currency)}</span>
        </p>
      </div>
    </div>
  );
}

/**
 * Component to display balance expiration info
 */
function ExpirationInfo({ expiresAt }: { expiresAt: string }) {
  const { t } = useTranslation();
  const expirationDate = new Date(expiresAt);
  const daysUntilExpiry = differenceInDays(expirationDate, new Date());

  // Color coding based on days until expiry
  const isExpiringSoon = daysUntilExpiry <= 30;
  const isExpired = daysUntilExpiry < 0;

  if (isExpired) {
    return (
      <div className="flex items-center gap-1.5 mt-1 text-red-600">
        <AlertTriangle className="size-3.5" />
        <span className="text-xs font-medium">{t('ads.wallet.balanceExpired', 'Solde expiré')}</span>
      </div>
    );
  }

  return (
    <div className={cn(
      'flex items-center gap-1.5 mt-1',
      isExpiringSoon ? 'text-orange-600' : 'text-muted-foreground'
    )}>
      <Clock className="size-3.5" />
      <span className="text-xs">
        {isExpiringSoon ? (
          t('ads.wallet.expiresIn', { days: daysUntilExpiry })
        ) : (
          t('ads.wallet.expiresOn', { date: format(expirationDate, 'dd MMM yyyy', { locale: fr }) })
        )}
      </span>
    </div>
  );
}
